import { motion } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Sparkles } from "lucide-react";
import StarBackground from "../components/StarBackground";
import { useState } from "react";

const translations = {
  en: {
    backToMyWork: "Back to My Work",
    title: "The Whirlpool Galaxy",
    subtitle: "Messier 51a (M51a) or NGC 5194",
    overview: "Overview",
    location: "Location",
    locationDesc: "The M51 Whirlpool Galaxy is located in the constellation Canes Venatici, near the handle of the Big Dipper.",
    distance: "Distance",
    distanceDesc: "It is approximately 31 million light-years from Earth.",
    size: "Size",
    sizeDesc: "The main galaxy (M51a) is about 76,900 light-years in diameter.",
    appearance: "Appearance",
    appearanceDesc: "Its face-on orientation and distinct, swirling spiral arms make it a popular target for amateur astronomers and astrophotographers.",
    visibility: "Visibility",
    visibilityDesc: "With an apparent magnitude of 8.4, it can be spotted with binoculars or a small telescope under dark skies, and is best observed during the spring.",
    interactionTitle: "Interaction with Companion Galaxy",
    interactionIntro: "The Whirlpool Galaxy's striking structure is largely due to its gravitational interaction with a smaller dwarf galaxy, NGC 5195.",
    tidalForces: "Tidal Forces",
    tidalForcesDesc: "The gravitational pull from NGC 5195 compresses gas in M51's disk, which triggers bursts of new star formation along its arms. These young, hot stars glow blue, while the vast hydrogen gas clouds glow pink or red in images.",
    activeNucleus: "Active Nucleus",
    activeNucleusDesc: "The larger galaxy has a Seyfert 2 active galactic nucleus, which is fueled by a supermassive black hole at its heart.",
    ongoingStudy: "Ongoing Study",
    ongoingStudyDesc: "This interacting pair is one of the most studied systems, helping astronomers understand how galaxies are shaped by gravitational encounters."
  },
  no: {
    backToMyWork: "Tilbake til Mitt Arbeid",
    title: "Virvelgalaksen",
    subtitle: "Messier 51a (M51a) eller NGC 5194",
    overview: "Oversikt",
    location: "Beliggenhet",
    locationDesc: "M51 Virvelgalaksen ligger i stjernebildet Jakthundene, nær håndtaket på Karlsvogna.",
    distance: "Avstand",
    distanceDesc: "Den er omtrent 31 millioner lysår fra Jorden.",
    size: "Størrelse",
    sizeDesc: "Hovedgalaksen (M51a) er omtrent 76 900 lysår i diameter.",
    appearance: "Utseende",
    appearanceDesc: "Dens frontvendte orientering og distinkte, virvlende spiralarmer gjør den til et populært mål for amatørastronomer og astrofotografer.",
    visibility: "Synlighet",
    visibilityDesc: "Med en tilsynelatende størrelse på 8,4 kan den oppdages med kikkert eller et lite teleskop under mørk himmel, og observeres best om våren.",
    interactionTitle: "Samspill med Følgegalakse",
    interactionIntro: "Virvelgalaksens slående struktur skyldes i stor grad dens gravitasjonelle samspill med en mindre dverggalakse, NGC 5195.",
    tidalForces: "Tidevannskrefter",
    tidalForcesDesc: "Gravitasjonstrekket fra NGC 5195 komprimerer gass i M51s skive, noe som utløser utbrudd av ny stjernedannelse langs armene. Disse unge, varme stjernene lyser blått, mens de store hydrogengasskysene lyser rosa eller rødt på bilder.",
    activeNucleus: "Aktiv Kjerne",
    activeNucleusDesc: "Den større galaksen har en Seyfert 2 aktiv galaktisk kjerne, som drives av et supermassivt sort hull i hjertet.",
    ongoingStudy: "Pågående Studier",
    ongoingStudyDesc: "Dette samvirkende paret er et av de mest studerte systemene, og hjelper astronomer å forstå hvordan galakser formes av gravitasjonsmøter."
  },
  pl: {
    backToMyWork: "Powrót do Mojej Pracy",
    title: "Galaktyka Wir",
    subtitle: "Messier 51a (M51a) lub NGC 5194",
    overview: "Przegląd",
    location: "Lokalizacja",
    locationDesc: "Galaktyka Wir M51 znajduje się w gwiazdozbiorze Psów Gończych, w pobliżu uchwytu Wielkiego Wozu.",
    distance: "Odległość",
    distanceDesc: "Jest oddalona o około 31 milionów lat świetlnych od Ziemi.",
    size: "Rozmiar",
    sizeDesc: "Główna galaktyka (M51a) ma około 76 900 lat świetlnych średnicy.",
    appearance: "Wygląd",
    appearanceDesc: "Jej orientacja frontem do nas i wyraźne, wirujące ramiona spiralne czynią ją popularnym celem dla astronomów amatorów i astrofotografów.",
    visibility: "Widoczność",
    visibilityDesc: "Z jasnością pozorną 8,4 można ją dostrzec przez lornetkę lub mały teleskop pod ciemnym niebem, a najlepiej obserwować ją wiosną.",
    interactionTitle: "Interakcja z Galaktyką Towarzyszącą",
    interactionIntro: "Uderzająca struktura Galaktyki Wir jest w dużej mierze wynikiem jej grawitacyjnej interakcji z mniejszą galaktyką karłowatą, NGC 5195.",
    tidalForces: "Siły Pływowe",
    tidalForcesDesc: "Przyciąganie grawitacyjne z NGC 5195 kompresuje gaz w dysku M51, co wyzwala wybuchy nowej formacji gwiazd wzdłuż jej ramion. Te młode, gorące gwiazdy świecą na niebiesko, podczas gdy ogromne chmury gazu wodorowego świecą na różowo lub czerwono na zdjęciach.",
    activeNucleus: "Aktywne Jądro",
    activeNucleusDesc: "Większa galaktyka ma aktywne jądro galaktyczne typu Seyferta 2, które jest zasilane przez supermasywną czarną dziurę w jej sercu.",
    ongoingStudy: "Trwające Badania",
    ongoingStudyDesc: "Ta interaktywna para jest jednym z najlepiej zbadanych układów, pomagając astronomom zrozumieć, jak galaktyki są kształtowane przez spotkania grawitacyjne."
  }
};

export default function WhirlpoolDetails() {
  const [searchParams] = useSearchParams();
  const [language, setLanguage] = useState(searchParams.get('lang') || 'en');
  const t = translations[language];

  return (
    <div className="min-h-screen">
      <StarBackground />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/5 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
          <Link to="/m31" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors duration-300">
            <ArrowLeft className="w-5 h-5" />
            <span>{t.backToMyWork}</span>
          </Link>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Sparkles className="text-cyan-400 w-6 h-6" />
              <span className="text-xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                {t.title}
              </span>
            </div>
            
            {/* Language Selector */}
            <div className="flex items-center gap-2 ml-4 pl-4 border-l border-white/20">
              <button 
                className={`transition-colors duration-300 px-3 py-1 rounded-full ${
                  language === 'en' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                onClick={() => setLanguage('en')}
                data-testid="lang-en-btn"
              >
                EN
              </button>
              <button 
                className={`transition-colors duration-300 px-3 py-1 rounded-full ${
                  language === 'no' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                onClick={() => setLanguage('no')}
                data-testid="lang-no-btn"
              >
                NO
              </button>
              <button 
                className={`transition-colors duration-300 px-3 py-1 rounded-full ${
                  language === 'pl' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                onClick={() => setLanguage('pl')}
                data-testid="lang-pl-btn"
              >
                PL
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <section className="relative pt-32 pb-24 px-8" data-testid="whirlpool-details">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="mb-12"
          >
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 text-center"
              style={{ fontFamily: 'Unbounded, sans-serif' }}
              data-testid="whirlpool-title"
            >
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400">
                {t.title}
              </span>
            </h1>
            <p className="text-lg text-white/90 text-center mb-8">
              {t.subtitle}
            </p>
          </motion.div>

          {/* Content Card */}
          <motion.div 
            className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 md:p-12 shadow-[0_8px_32px_rgba(0,0,0,0.3)] space-y-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            {/* Overview */}
            <div>
              <h2 
                className="text-3xl font-bold mb-6"
                style={{ fontFamily: 'Unbounded, sans-serif' }}
              >
                {t.overview}
              </h2>
              
              <div className="space-y-6 text-white/80 leading-relaxed">
                <div>
                  <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                    {t.location}
                  </h3>
                  <p>{t.locationDesc}</p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                    {t.distance}
                  </h3>
                  <p>{t.distanceDesc}</p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                    {t.size}
                  </h3>
                  <p>{t.sizeDesc}</p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                    {t.appearance}
                  </h3>
                  <p>{t.appearanceDesc}</p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                    {t.visibility}
                  </h3>
                  <p>{t.visibilityDesc}</p>
                </div>
              </div>
            </div>

            {/* Interaction Section */}
            <div className="pt-6 border-t border-white/10">
              <h2 
                className="text-3xl font-bold mb-6"
                style={{ fontFamily: 'Unbounded, sans-serif' }}
              >
                {t.interactionTitle}
              </h2>

              <p className="text-white/90 text-lg leading-relaxed mb-6">
                {t.interactionIntro}
              </p>

              <div className="space-y-6 text-white/80 leading-relaxed">
                <div>
                  <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                    {t.tidalForces}
                  </h3>
                  <p>{t.tidalForcesDesc}</p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                    {t.activeNucleus}
                  </h3>
                  <p>{t.activeNucleusDesc}</p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                    {t.ongoingStudy}
                  </h3>
                  <p>{t.ongoingStudyDesc}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
